// NOTE: In the future if performance becomes an issue, we can memoize the functions

import { unref } from 'vue';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import * as Sentry from '@sentry/vue';

// Flexar: the report context gets the shape of the payload, not the payload. API responses pass through
// here — conversations, contacts, messages — and top-level fields survive the SDK's normalisation (depth 3):
// verified on the envelope, `content` carrying the conversation text arrived in full. Field names are the
// useful part for debugging a key-transform failure.
// See flexar-docs/kb/adr/2026-08-29-glitchtip-error-reporting.md.
const describePayload = payload => {
  const value = unref(payload);
  if (Array.isArray(value)) {
    return `array(${value.length})`;
  }
  return value && typeof value === 'object' ? Object.keys(value) : typeof value;
};

/**
 * Vue composable that converts object keys to camelCase
 * @param {Object|Array|import('vue').Ref<Object|Array>} payload - Object or array to convert
 * @param {Object} [options] - Options object
 * @param {boolean} [options.deep=false] - Should convert keys of nested objects
 * @returns {Object|Array} Converted payload with camelCase keys
 */
export function useCamelCase(payload, options) {
  try {
    const unrefPayload = unref(payload);
    return camelcaseKeys(unrefPayload, options);
  } catch (e) {
    Sentry.setContext('transform-keys-error', {
      // Flexar: the shape, never the payload — see describePayload above.
      payloadShape: describePayload(payload),
      options,
      op: 'camelCase',
    });
    Sentry.captureException(e);
    return payload;
  }
}

/**
 * Vue composable that converts object keys to snake_case
 * @param {Object|Array|import('vue').Ref<Object|Array>} payload - Object or array to convert
 * @param {Object} [options] - Options object
 * @param {boolean} [options.deep=false] - Should convert keys of nested objects
 * @returns {Object|Array} Converted payload with snake_case keys
 */
export function useSnakeCase(payload, options) {
  try {
    const unrefPayload = unref(payload);
    return snakecaseKeys(unrefPayload, options);
  } catch (e) {
    Sentry.setContext('transform-keys-error', {
      // Flexar: the shape, never the payload — see describePayload above.
      payloadShape: describePayload(payload),
      options,
      op: 'snakeCase',
    });
    Sentry.captureException(e);
    return payload;
  }
}
